// Undo (Parity P1, wave 0; hybrid design §1 principle 3, codex #7). A reversible write returns
// `undo_id`; `POST undo/{id}` applies its compensation through the normal write path (the
// write's own events and permissions fire, then `task.undone`). Refusals are structured:
// `undo_expired` (past 15 minutes), `undo_stale` (the subject changed since; `detail` says who
// changed what), `undo_not_allowed` (only who made the write, or the person a crew acted for).
// A repeated call by the same caller answers the first outcome again (`replay: true`).
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";
import { TaskDetail } from "./work";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const UndoResult = z
  .object({
    undo_id: Uuid,
    kind: z.string().describe("The compensation's kind (`task.status`, `task.fields`, …)"),
    subject: z.object({ kind: z.string(), id: Uuid }),
    used_at: Timestamp,
    replay: z.boolean().describe("True when this caller had already undone it (nothing ran)"),
    task: TaskDetail.nullable().describe("The task as it now is, when the subject is a task"),
  })
  .meta({ id: "UndoResult" });

/**
 * AGENT-GUARDRAILS: a write the caller may still undo (its own, or an agent's: a person undoes a
 * DE's or crew's write from the task's Activity for its 15 minutes).
 */
export const UndoableWrite = z
  .object({
    undo_id: Uuid,
    kind: z.string().describe("The compensation's kind (`task.status`, `task.fields`, …)"),
    /** The write's last event (the Activity row the Undo sits on); null for an old record. */
    event_id: Uuid.nullable(),
    actor: z.object({
      kind: z.string(),
      membership_id: Uuid.nullable(),
      name: z.string().nullable(),
    }),
    created_at: Timestamp,
    expires_at: Timestamp,
  })
  .meta({ id: "UndoableWrite" });

export const UndoList = z.object({ items: z.array(UndoableWrite) }).meta({ id: "UndoList" });

/**
 * DECISION-LEDGER: why the write is undone (optional). Kept on `task.undone` and, for an agent's
 * write, on its reversal in the agent's record, beside who undid it.
 */
export const UndoApply = z
  .strictObject({ reason: z.string().trim().max(500).optional() })
  .meta({ id: "UndoApply" });

export const UndoListQuery = z.object({
  subject_kind: z.enum(["task"]),
  subject_id: Uuid,
});

export const undoRoutes = {
  "undo.list": defineRoute({
    ...core,
    id: "undo.list",
    method: "get",
    path: `${base}/undo`,
    tag: "undo",
    summary:
      "The writes on a subject the caller may still undo (its own; a person: an agent's too)",
    access: "member",
    params: CompanyParams,
    query: UndoListQuery,
    response: { status: 200, schema: UndoList },
    errors: [],
    emits: [],
  }),
  "undo.apply": defineRoute({
    ...core,
    id: "undo.apply",
    method: "post",
    path: `${base}/undo/{id}`,
    tag: "undo",
    summary: "Undo a write from its undo record (same actor, within 15 minutes, subject unchanged)",
    access: "member",
    params: CompanyItemParams,
    body: UndoApply,
    response: { status: 200, schema: UndoResult },
    errors: [
      "undo_expired",
      "undo_stale",
      "undo_not_allowed",
      "status_invalid",
      "dependency_blocked",
      "conflict",
    ],
    emits: [
      "task.undone",
      "task.status_changed",
      "task.updated",
      "task.due_changed",
      "task.priority_changed",
      "task.field_changed",
      "task.moved",
    ],
  }),
} as const;
